import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/SEO';

const About = () => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <>
      <SEO
        title="Sobre Mí | Portfolio"
        description="Conoce mi trayectoria como diseñador gráfico y fotógrafo. Más de 8 años creando experiencias visuales únicas."
      />

      <div className="pt-32 pb-24">
        {/* Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-24" data-testid="about-header">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div
              ref={headerRef}
              initial={{ opacity: 0, y: 30 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4">
                Sobre Mí
              </p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8" data-testid="about-title">
                Diseño con
                <br />
                propósito y pasión
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="px-6 md:px-12 lg:px-24" data-testid="about-content">
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
              {/* Image */}
              <motion.div
                ref={contentRef}
                initial={{ opacity: 0, x: -30 }}
                animate={contentInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="order-2 md:order-1"
              >
                <div className="editorial-border sticky top-32">
                  <img
                    src="https://images.unsplash.com/photo-1759247178379-0e8eba83a4a6?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Portrait"
                    className="w-full h-[600px] object-cover grayscale"
                    data-testid="about-image"
                  />
                </div>
              </motion.div>

              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={contentInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="order-1 md:order-2 space-y-8"
              >
                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-4">Mi Historia</h2>
                  <p className="text-lg font-light leading-relaxed text-ink/80">
                    Desde que descubrí el poder del diseño visual, he dedicado mi carrera a explorar
                    la intersección entre estética y funcionalidad. Mi enfoque se caracteriza por un diseño
                    minimalista y elegante, donde cada elemento tiene un propósito y contribuye a la armonía visual del conjunto.
                  </p>
                </div>

                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-4">Filosofía</h2>
                  <p className="text-lg font-light leading-relaxed text-ink/80">
                    Creo que cada proyecto tiene una historia única que contar. Mi trabajo no se trata
                    solo de crear imágenes hermosas, sino de comunicar ideas poderosas que resuenen con
                    las personas y dejen una impresión duradera.
                  </p>
                </div>

                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-4">Especializaciones</h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-editorial mt-3 flex-shrink-0" />
                      <span className="text-lg font-light leading-relaxed text-ink/80">
                        Identidad visual y branding corporativo
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-editorial mt-3 flex-shrink-0" />
                      <span className="text-lg font-light leading-relaxed text-ink/80">
                        Fotografía editorial y comercial
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-editorial mt-3 flex-shrink-0" />
                      <span className="text-lg font-light leading-relaxed text-ink/80">
                        Diseño de campañas digitales e impresas
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-editorial mt-3 flex-shrink-0" />
                      <span className="text-lg font-light leading-relaxed text-ink/80">
                        Maquetación editorial y publicitaria
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-editorial mt-3 flex-shrink-0" />
                      <span className="text-lg font-light leading-relaxed text-ink/80">
                        Dirección de arte y conceptualización creativa
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-4">Experiencia</h2>
                  <p className="text-lg font-light leading-relaxed text-ink/80">
                    Con más de 4 años de experiencia trabajando con clientes de diversos sectores,
                    desde startups innovadoras hasta marcas establecidas, he desarrollado una
                    comprensión profunda de cómo el diseño puede impulsar el éxito empresarial.
                  </p>
                </div>

                <div>
                  <h2 className="text-3xl font-bold tracking-tight mb-4">Enfoque de Trabajo</h2>
                  <p className="text-lg font-light leading-relaxed text-ink/80">
                    Mi proceso creativo se basa en la colaboración cercana con mis clientes. Creo
                    en escuchar, entender y traducir visiones en realidades visuales que superen
                    expectativas. Cada proyecto es una oportunidad para innovar y crear algo
                    verdaderamente memorable.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="px-6 md:px-12 lg:px-24 mt-32" data-testid="about-stats">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-12 py-16 border-t border-b border-ink/10"
            >
              <div className="text-center">
                <p className="text-5xl md:text-6xl font-bold tracking-tight mb-2" data-testid="stat-years">4+</p>
                <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Años</p>
              </div>
              <div className="text-center">
                <p className="text-5xl md:text-6xl font-bold tracking-tight mb-2" data-testid="stat-projects">60+</p>
                <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Proyectos</p>
              </div>
              <div className="text-center">
                <p className="text-5xl md:text-6xl font-bold tracking-tight mb-2" data-testid="stat-clients">20+</p>
                <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Clientes</p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;